<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up deck-folder">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h3>{{ folder ? 'Edit Folder' : 'New Folder' }}</h3>
            <input id="deck-folder-name" type="text" v-model="name" placeholder="Folder name" maxlength="100" @keyup.enter="submit()" />

            <div class="deck-folder-h">
                <h3>Decks</h3>
                <span>{{ selected.length }} selected</span>
            </div>
            <input v-if="boards.length > 8" type="text" v-model="searchQ" placeholder="Search decks" aria-label="Search decks" />
            <div class="deck-folder-list">
                <label v-for="board in shownBoards" :key="board.Slug" :class="selected.includes(board.Slug) ? 'deck-folder-row on' : 'deck-folder-row'">
                    <input type="checkbox" :value="board.Slug" v-model="selected" />
                    <span class="deck-folder-name">{{ board.Name }}</span>
                    <span v-if="board.IsTaskManager && board.OwnerName" class="deck-folder-owner">{{ board.OwnerName }}</span>
                </label>
                <div v-if="!shownBoards.length" class="deck-folder-empty">{{ boards.length ? 'No decks match that search.' : 'No decks yet.' }}</div>
            </div>

            <div class="deck-folder-actions">
                <input :class="disableButton ? 'disable-button' : ''" type="submit" :value="folder ? 'Save' : 'Create Folder'" @click="submit()" />
                <button v-if="folder" type="button" class="deck-folder-delete" @click="remove()">Delete Folder</button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';

// Makes or edits one of the person's deck folders (API routes/deckfolders.ts): a name and which decks are in it.
// boards: the decks the person can pick from (the Decks page's list). folder: { ID, Name, Slugs } to edit, or
// none for a new one. Emits saved(ID) after a save, deleted after a delete.
export default defineComponent({
    name: 'DeckFolderModal',
    props: {
        boards: { type: Array as () => any[], required: true },
        folder: { type: Object, default: null }
    },
    emits: [ 'close', 'saved', 'deleted' ],
    data() {
        return {
            name: this.folder ? this.folder.Name : '',
            // Decks it lists that this person can no longer open stay in it untouched, but aren't shown
            selected: this.folder ? [ ...this.folder.Slugs ] : [] as string[],
            searchQ: '',
            disableButton: false
        }
    },
    computed: {
        // Ticked decks first (as they were when opened, so rows don't jump while ticking), then by name
        shownBoards(): any[] {
            const q = this.searchQ.trim().toLowerCase();
            const inFolder = new Set(this.folder ? this.folder.Slugs : []);
            return this.boards
                .filter((b: any) => !q || b.Name.toLowerCase().includes(q) || (b.OwnerName || '').toLowerCase().includes(q))
                .sort((a: any, b: any) => Number(inFolder.has(b.Slug)) - Number(inFolder.has(a.Slug)) || a.Name.localeCompare(b.Name));
        }
    },
    mounted() {
        document.getElementById('deck-folder-name')?.focus();
    },
    methods: {
        async submit() {
            if(this.disableButton) {
                return;
            }
            if(!this.name.trim()) {
                alert('Give the folder a name.');
                return;
            }
            this.disableButton = true;
            const body = { name: this.name.trim(), slugs: this.selected };
            const request = this.folder
                ? axios.post(`${this.$store.state.apiUrl}/api/deckfolders/${this.folder.ID}`, body).then(() => this.folder.ID)
                : axios.post(`${this.$store.state.apiUrl}/api/deckfolders`, body).then(resp => resp.data.ID);
            await request
                .then(id => {
                    this.$emit('saved', id);
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.disableButton = false;
        },
        async remove() {
            if(this.disableButton || !confirm(`Delete the folder "${this.folder.Name}"? The decks in it aren't deleted.`)) {
                return;
            }
            this.disableButton = true;
            await axios.delete(`${this.$store.state.apiUrl}/api/deckfolders/${this.folder.ID}`)
                .then(() => {
                    this.$emit('deleted');
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.disableButton = false;
        }
    }
});
</script>

<style scoped>
/* As in AddCompanyBoard: a flex column inside the capped popup, where only the deck list scrolls */
.deck-folder { display: flex; flex-direction: column; overflow: hidden; }
.deck-folder > *:not(.form-close) { flex-shrink: 0; }
.deck-folder-h { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.deck-folder-h span { font-size: 12.5px; color: var(--font-dark-gray); }
.deck-folder .deck-folder-list { flex: 1 1 auto; min-height: 90px; overflow: auto; display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px; }
.deck-folder-row { display: flex; align-items: center; gap: 10px; border: 1px solid var(--mid-gray); border-radius: 8px; padding: 9px 12px; cursor: pointer; }
.deck-folder-row.on { border-color: var(--primary-color); }
.deck-folder-row input[type=checkbox] { width: auto; flex-shrink: 0; }
.deck-folder-name { flex: 1; min-width: 0; font-size: 14px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.deck-folder-owner { font-size: 12px; color: var(--font-dark-gray); white-space: nowrap; }
.deck-folder-empty { font-size: 13px; color: var(--font-dark-gray); padding: 6px 2px; }
.deck-folder-actions { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.deck-folder-actions input[type=submit] { width: auto; margin-bottom: 0; }
.deck-folder-delete { margin-top: 10px; background: none; border: none; padding: 0; font: inherit; font-size: 13px; font-weight: 600; color: var(--primary-color); cursor: pointer; }
.deck-folder-delete:hover { text-decoration: underline; }
</style>
